import { LuIcon, Menu, type MenuClickTriggerProps, type MenuRootProps, type MenuVariantProps } from "@overmap-ai/blocks"
import type { User } from "@overmap-ai/common"
import { useSort, useTextFilter } from "@overmap-ai/hooks"
import { memo, type ReactElement } from "react"

import { assigneeFilterCallback } from "../../filtering/utils"
import { modelIconMapping } from "../../mappings/icons"
import { assigneeSortFn } from "../../sorting/utils"
import {
	AssigneeMultiSelectGroup,
	type AssigneeMultiSelectGroupProps,
} from "../MenuMultiSelectGroups/AssigneeMultiSelectGroup"

interface AssigneeMultiSelectProps
	extends
		MenuRootProps,
		AssigneeMultiSelectGroupProps,
		MenuVariantProps,
		Omit<MenuClickTriggerProps, "children" | "ref"> {
	children: ReactElement
}

export const AssigneeMultiSelect = memo((props: AssigneeMultiSelectProps) => {
	const { children, values, onValuesChange, users, disabled, variant, size, accentColor, ...rest } = props

	const sortedUsers = useSort(users, assigneeSortFn)

	const [filteredUsers, filterValue, setFilterValue] = useTextFilter<User | null>(sortedUsers, assigneeFilterCallback)

	const isEmpty = filteredUsers.length === 0

	return (
		<Menu.Root {...rest}>
			<Menu.ClickTrigger disabled={disabled}>{children}</Menu.ClickTrigger>
			<Menu.Content variant={variant} size={size} accentColor={accentColor}>
				<Menu.InputRoot>
					<Menu.InputSlot>
						<LuIcon icon={modelIconMapping.search} />
					</Menu.InputSlot>
					<Menu.InputField placeholder="Search" value={filterValue} onValueChange={setFilterValue} />
				</Menu.InputRoot>
				{!isEmpty ? <Menu.Separator /> : null}
				<Menu.Scroll>
					{!isEmpty ? (
						<AssigneeMultiSelectGroup
							values={values}
							users={filteredUsers}
							onValuesChange={onValuesChange}
						/>
					) : null}
				</Menu.Scroll>
			</Menu.Content>
		</Menu.Root>
	)
})
AssigneeMultiSelect.displayName = "AssigneeMultiSelect"
